import { computed, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { signalStore, withState, withComputed, withMethods, withHooks } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { Subject, debounceTime, filter } from 'rxjs';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { GALLERIES_DATA, type GalleryOpFailure } from '../infrastructure/galleries.data';
import { matchesSearchQuery } from '../../util/search-query';
import {
  DEFAULT_GALLERY_SORT,
  sortGalleries,
  type GalleryCategoryFilterOption,
  type GalleryListItem,
  type GallerySort,
  type GalleryStatus,
  type GalleryTrashItem,
} from '../entities/galleries.models';
import { environment } from '@environment';

export type GalleriesStatus = 'idle' | 'loading' | 'ready' | 'error';
export type CreateStatus = 'idle' | 'creating' | 'error';

export interface BatchFailure {
  id: string;
  title: string;
  reason: GalleryOpFailure;
}

/** What a batch settled into. An EMPTY outcome means the store already spoke (a whole-request
 *  failure toast, or nothing to do) — the page opens no result dialog for it. */
export interface BatchOutcome {
  ok: string[];
  failed: BatchFailure[];
}

const EMPTY_OUTCOME: BatchOutcome = { ok: [], failed: [] };

const PER_PAGE = 24;

// A filtered read (search or category) asks for the server's page ceiling instead of the
// dashboard page, so the narrowed answer usually arrives whole. «Показать ещё» stays and
// loads the rest within the same filter when it does not.
const SEARCH_PER_PAGE = 100;

// The typed word lands in state at once (the field echoes every keystroke), but the server read
// waits out a pause — the result strip appears when it lands.
const QUERY_DEBOUNCE_MS = 300;
// Печать идёт в поток: чтение уходит после паузы. У корзины своё ожидание — два экрана печатают
// в разные вопросы; `false` снимает уже отложенное чтение («Сбросить фильтр»).
const queryTyped = new Subject<void>();
const trashQueryTyped = new Subject<boolean>();

// Source ids whose duplicate request is still in flight. Module scope is one set
// per store — the store is providedIn:'root', so there is exactly one instance.
const duplicatesInFlight = new Set<string>();

// Monotonic stamp over ALL list-writing requests (load, load-more, the post-create
// and post-duplicate refreshes). Each new request invalidates every older in-flight
// response — including same-category races (initial load vs create refresh, stale
// load-more cursor) that a categoryId-equality guard cannot tell apart.
let listGeneration = 0;

// Same stamp, for the shell's count-only trash reads.
let trashCountGeneration = 0;

// Same stamp over the category vocabulary read — a second open must not be answered by the first.
let categoriesGeneration = 0;

// A batch is a client loop over the per-gallery routes (no bulk endpoint exists). Four requests in
// flight keeps a 24-row page quick without burying the gateway under one photographer's click.
const BATCH_CONCURRENCY = 4;

// Fixed-size worker pool: `run` is applied to every id, at most BATCH_CONCURRENCY at a time,
// answers land back in the id order so a row's verdict never drifts to its neighbour.
async function runBatch<T>(ids: readonly string[], run: (id: string) => Promise<T>): Promise<T[]> {
  const results = new Array<T>(ids.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < ids.length) {
      const index = next++;
      results[index] = await run(ids[index]);
    }
  };

  await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, ids.length) }, worker));
  return results;
}

type BatchVerdict = 'ok' | 'partial' | 'failed';

// The three endings of a batch, and the toast each one owes the photographer. `failed` is the
// «весь запрос упал» case — nothing landed and every refusal was a transport/server error, so the
// selection stays untouched and no result dialog opens.
function settleBatch(outcome: BatchOutcome, deleting: boolean, toast: ToastService): BatchVerdict {
  if (outcome.failed.length === 0) {
    toast.success(deleting ? `В корзину перемещено: ${outcome.ok.length}` : `Категории обновлены: ${outcome.ok.length}`);
    return 'ok';
  }

  if (outcome.ok.length === 0 && outcome.failed.every(entry => entry.reason === 'error')) {
    toast.error(deleting ? 'Не удалось удалить галереи' : 'Не удалось изменить категории');
    return 'failed';
  }

  return 'partial';
}

// The trash routes answer with a bare boolean, so every refusal reads as `error` — which is the
// one reason the result dialog spells «ошибка». What it costs: an all-refused batch is read as
// «весь запрос упал», exactly as a list batch of pure errors already is.
function trashOutcome(targets: readonly { id: string; title: string }[], results: readonly boolean[]): BatchOutcome {
  return {
    ok: targets.filter((_, index) => results[index]).map(item => item.id),
    failed: targets
      .map((item, index) => ({ id: item.id, title: item.title, reason: 'error' as const, ok: results[index] }))
      .filter(entry => !entry.ok)
      .map(({ id, title, reason }) => ({ id, title, reason })),
  };
}

// Same three endings as `settleBatch`, with the trash's own wording (spec § 15–16).
function settleTrashBatch(outcome: BatchOutcome, done: string, allFailed: string, toast: ToastService): BatchVerdict {
  if (outcome.failed.length === 0) {
    toast.success(done);
    return 'ok';
  }

  if (outcome.ok.length === 0) {
    toast.error(allFailed);
    return 'failed';
  }

  return 'partial';
}

// Same stamp over ALL trash-writing operations — reads (loadTrash, loadMoreTrash) AND the
// optimistic row removals (restore, purge, emptyTrash). An in-flight page landing after a
// removal would resurrect the row, and «Восстановить» on that ghost 404s.
let trashGeneration = 0;

// Сервер чистит корзину пачками — «Очистить корзину» повторяет запрос, но не дольше этого числа раз.
const EMPTY_TRASH_MAX_REQUESTS = 20;

// Счётчик шапки живёт на серверном итоге, поэтому строка, ушедшая оптимистично, уменьшает и его
// (отрицательное `gone` — возврат строки после отказа). До первого ответа считать нечего.
function trashTotalAfter(total: number | null, gone: number): number | null {
  return total === null ? null : Math.max(0, total - gone);
}

interface GalleriesState {
  items: GalleryListItem[];
  status: GalleriesStatus;
  // A read running over data already on screen (a settled search word, a category click).
  // The table keeps the previous rows dimmed instead of collapsing into skeletons
  // (.claude/rules/loading-states.md — reload keeps last data).
  refreshing: boolean;
  createStatus: CreateStatus;
  trashItems: GalleryTrashItem[];
  trashStatus: GalleriesStatus;
  categoryOptions: GalleryCategoryFilterOption[];
  categoryStatus: GalleriesStatus;
  categoryIds: string[];
  // The order the list is READ in. It lives here, not on the page, so that «what order is this
  // list in» has one answer: `filteredItems` is literally what the screen shows, which is what
  // a Shift+click range spans (spec § 37).
  sort: GallerySort;
  // Every ticked id, INCLUDING the ones a filter currently hides: dropping the filter must bring
  // them back into the selection (spec § 36). What the bar counts and the batches touch is the
  // EFFECTIVE selection — this set narrowed to what is on screen (`selectedIds`).
  selection: string[];
  // Where the last plain click landed; Shift+click spans from here to the clicked row.
  selectionAnchor: string | null;
  batchRunning: boolean;
  // The search words of the galleries list. The trash types its own (`trashQuery`) — two
  // screens, two questions, each read with its own word.
  query: string;
  listCursor: string | null;
  listHasMore: boolean;
  loadingMore: boolean;
  trashCursor: string | null;
  trashHasMore: boolean;
  // Нефильтрованный серверный счёт корзины — его показывает вкладка. Его пишет только
  // `refreshTrashCount`: чтение списка знает лишь итог своего поиска и своих категорий.
  trashTotal: number;
  // Счёт корзины уже отвечен (или отказал): до этого пустой список не значит пустой кабинет.
  trashTotalKnown: boolean;
  // Серверный итог ТЕКУЩЕГО чтения корзины (слово поиска + категории) — счётчик в шапке.
  // `null` — ответа на этот вопрос ещё нет.
  trashListTotal: number | null;
  trashLoadingMore: boolean;
  // The trash screen picks its own rows. A separate list from `selection`: the two screens show
  // two different collections, and a gallery restored out of one must not stay ticked in the other.
  trashSelection: string[];
  trashSelectionAnchor: string | null;
  // Спека «Корзина» § 4: `null` — серверный порядок «сначала те, что удалятся раньше». Его нет
  // в меню, поэтому он и не выражается пунктом: выбор любого из пяти его перекрывает.
  trashSort: GallerySort | null;
  trashCategoryIds: string[];
  // Слова поиска корзины: их компилирует сервер — тем же грамматикой, что и у «Галерей».
  trashQuery: string;
}

const initialState: GalleriesState = {
  items: [],
  status: 'idle',
  refreshing: false,
  createStatus: 'idle',
  trashItems: [],
  trashStatus: 'idle',
  categoryOptions: [],
  categoryStatus: 'idle',
  categoryIds: [],
  sort: DEFAULT_GALLERY_SORT,
  selection: [],
  selectionAnchor: null,
  batchRunning: false,
  query: '',
  listCursor: null,
  listHasMore: false,
  loadingMore: false,
  trashCursor: null,
  trashHasMore: false,
  trashTotal: 0,
  trashTotalKnown: false,
  trashListTotal: null,
  trashLoadingMore: false,
  trashSelection: [],
  trashSelectionAnchor: null,
  trashSort: null,
  trashCategoryIds: [],
  trashQuery: '',
};

export const GalleriesStore = signalStore(
  { providedIn: 'root' },
  environment.storeWithDevTools('galleries'),
  withState<GalleriesState>(initialState),
  withComputed(store => {
    // A search or a category narrows the list; both are server-side, both read with the bigger page.
    const filtering = computed(() => store.query().trim().length > 0 || store.categoryIds().length > 0);
    // The dashboard render source, in screen order. Search and category filter are both server-side
    // (the list API paginates within them); this client pass is only a residual guard
    // so mid-flight/optimistic rows can't leak across a filter — it speaks the SAME
    // grammar the server compiles into SQL. The chosen sort is applied HERE, so nothing
    // downstream (the count, the effective selection, a Shift range) can read a different order.
    const filteredItems = computed(() => {
      const ids = store.categoryIds();
      const query = store.query();
      const narrowed = store
        .items()
        .filter(item => (ids.length === 0 || ids.some(id => item.categoryIds.includes(id))) && matchesSearchQuery(item, query));
      return sortGalleries(narrowed, store.sort());
    });
    // The EFFECTIVE selection: ticked ∩ on screen, in screen order. Everything the photographer sees
    // and everything a batch touches reads this — a gallery hidden by the current search or category
    // filter is not in the count, not in a dialog list, and not written to (spec § 36).
    const selectedIds = computed(() => {
      const selection = store.selection();
      if (selection.length === 0) return [];
      return filteredItems()
        .map(item => item.id)
        .filter(id => selection.includes(id));
    });
    // The trash's effective selection: ticked ∩ still in the trash, in screen order. A row a
    // restore or a purge already took is out of the count, out of every dialog list and out of
    // every batch — the same law the galleries list follows.
    const trashSelectedIds = computed(() => {
      const selection = store.trashSelection();
      if (selection.length === 0) return [];
      return store
        .trashItems()
        .map(item => item.id)
        .filter(id => selection.includes(id));
    });
    // Спека «Корзина» § 5: фильтр предлагает категории ИЗ галерей корзины, не весь словарь.
    // Уже выбранная остаётся в списке, даже когда сузила корзину до строк без неё, — иначе
    // снять свой же фильтр было бы нечем.
    const trashCategoryOptions = computed(() => {
      const present = new Set(store.trashItems().flatMap(item => item.categoryIds));
      const picked = store.trashCategoryIds();
      return store.categoryOptions().filter(option => present.has(option.id) || picked.includes(option.id));
    });
    return {
      filtering,
      filteredItems,
      selectedIds,
      trashSelectedIds,
      trashCategoryOptions,
      // What the strip counts is what the list shows — never a number the page contradicts.
      resultCount: computed(() => filteredItems().length),
      pageSize: computed(() => (filtering() ? SEARCH_PER_PAGE : PER_PAGE)),
    };
  }),
  withMethods((store, data = inject(GALLERIES_DATA), toast = inject(ToastService)) => ({
    async load(): Promise<void> {
      const gen = ++listGeneration;
      // A COLD read (nothing on screen yet) shows skeletons; a WARM one — a settled search
      // word, a category click — holds the rows it already has and only dims them.
      const warm = store.items().length > 0;
      updateState(store, '[Galleries] Load Start', warm ? { refreshing: true } : { status: 'loading' });
      const page = await data.list({ perPage: store.pageSize(), categoryId: store.categoryIds(), q: store.query() });
      // RACE-SAFE: any newer list request (reload, filter change, load-more,
      // create/duplicate refresh) owns the list now — this stale page must not
      // overwrite it.
      if (gen !== listGeneration) return;
      if (page) {
        updateState(store, '[Galleries] Load Ready', {
          items: page.items,
          status: 'ready',
          refreshing: false,
          listCursor: page.nextCursor,
          listHasMore: page.hasMore,
        });
      } else {
        updateState(store, '[Galleries] Load Failed', { status: 'error', refreshing: false });
      }
    },

    // Append the next keyset page to the accumulated list (the load-more action).
    async loadMore(): Promise<void> {
      if (!store.listHasMore() || store.loadingMore()) return;
      const gen = ++listGeneration;
      updateState(store, '[Galleries] Load More Start', { loadingMore: true });
      const page = await data.list({
        cursor: store.listCursor(),
        perPage: store.pageSize(),
        categoryId: store.categoryIds(),
        q: store.query(),
      });
      // RACE-SAFE: same stale-guard as load() — a newer list request (filter
      // change, reload, create/duplicate refresh) restarted the keyset, so this
      // cursor page belongs to a list that no longer exists and must not append.
      if (gen !== listGeneration) {
        updateState(store, '[Galleries] Load More Stale', { loadingMore: false });
        return;
      }

      if (page) {
        updateState(store, '[Galleries] Load More Ready', {
          items: [...store.items(), ...page.items],
          listCursor: page.nextCursor,
          listHasMore: page.hasMore,
          loadingMore: false,
        });
      } else {
        updateState(store, '[Galleries] Load More Failed', { loadingMore: false });
        toast.error('Не удалось загрузить ещё галереи. Попробуйте ещё раз.');
      }
    },

    // The category vocabulary for the dashboard filter chips. A null read leaves
    // the chips empty (the filter simply doesn't appear) — the list still loads.
    // `categoryStatus` is what the batch-category dialog shows as загрузка / ошибка / готово.
    async loadCategories(): Promise<void> {
      const gen = ++categoriesGeneration;
      updateState(store, '[Galleries] Categories Loading', { categoryStatus: 'loading' });
      const options = await data.categoryVocabulary();
      // RACE-SAFE: a newer vocabulary read owns the chips now.
      if (gen !== categoriesGeneration) return;
      if (options) updateState(store, '[Galleries] Categories Loaded', { categoryOptions: options, categoryStatus: 'ready' });
      else updateState(store, '[Galleries] Categories Failed', { categoryStatus: 'error' });
    },

    // «Выбрать все» отмечает всё, что сейчас на экране. Якорь не двигается: якорь — это последний
    // клик без Shift (§ 37), а не последняя строка массовой отметки.
    selectAllVisible(): void {
      const visible = store.filteredItems().map(item => item.id);
      updateState(store, '[Galleries] Select All', {
        selection: Array.from(new Set([...store.selection(), ...visible])),
      });
    },

    // A row was clicked. Plain click toggles it and becomes the new anchor; Shift+click ADDS the
    // whole screen-order span from the anchor to here and leaves the anchor where it was (spec § 37).
    toggleSelected(id: string, shift: boolean): void {
      const order = store.filteredItems().map(item => item.id);
      if (!order.includes(id)) return;
      const anchor = store.selectionAnchor();
      const selection = store.selection();
      if (shift && anchor && order.includes(anchor)) {
        const from = order.indexOf(anchor);
        const to = order.indexOf(id);
        const range = order.slice(Math.min(from, to), Math.max(from, to) + 1);
        updateState(store, '[Galleries] Select Range', { selection: Array.from(new Set([...selection, ...range])) });
        return;
      }

      updateState(store, '[Galleries] Toggle Selected', {
        selection: selection.includes(id) ? selection.filter(entry => entry !== id) : [...selection, id],
        selectionAnchor: id,
      });
    },

    clearSelection(): void {
      updateState(store, '[Galleries] Clear Selection', { selection: [], selectionAnchor: null });
    },

    // «Удалить» from the selection bar: one DELETE per gallery, four in flight. The galleries that
    // went to the trash leave the list; the ones that refused keep their row and become the new
    // selection so «Повторить» drives exactly them.
    async batchDelete(ids: readonly string[]): Promise<BatchOutcome> {
      if (store.batchRunning()) return EMPTY_OUTCOME;
      const targets = store.items().filter(item => ids.includes(item.id));
      if (targets.length === 0) return EMPTY_OUTCOME;
      updateState(store, '[Galleries] Batch Delete Start', { batchRunning: true });

      const results = await runBatch(
        targets.map(item => item.id),
        id => data.delete(id),
      );
      const outcome: BatchOutcome = {
        ok: targets.filter((_, index) => results[index] === null).map(item => item.id),
        failed: targets
          .map((item, index) => ({ id: item.id, title: item.title, reason: results[index] }))
          .filter((entry): entry is BatchFailure => entry.reason !== null),
      };
      const verdict = settleBatch(outcome, true, toast);

      // The rows leave AFTER the awaits, so the removal owns a fresh generation: a list read that
      // left before the batch would otherwise land and resurrect what is already in the trash.
      ++listGeneration;
      const deleted = new Set(outcome.ok);
      updateState(store, '[Galleries] Batch Delete Done', {
        items: store.items().filter(item => !deleted.has(item.id)),
        batchRunning: false,
        selection: verdict === 'ok' ? [] : verdict === 'partial' ? outcome.failed.map(entry => entry.id) : store.selection(),
        selectionAnchor: verdict === 'failed' ? store.selectionAnchor() : null,
      });
      if (outcome.ok.length > 0) await this.refreshTrashCount();
      return verdict === 'failed' ? EMPTY_OUTCOME : outcome;
    },

    // «Сменить категорию»: the SAME diff (add / remove / freshly typed names) written once per
    // gallery through the per-gallery apply route — the server does the ownership and 3-cap checks.
    async batchSetCategories(
      ids: readonly string[],
      add: readonly string[],
      remove: readonly string[],
      createNames: readonly string[] = [],
    ): Promise<BatchOutcome> {
      if (store.batchRunning()) return EMPTY_OUTCOME;
      const targets = store.items().filter(item => ids.includes(item.id));
      if (targets.length === 0 || (add.length === 0 && remove.length === 0 && createNames.length === 0)) {
        return EMPTY_OUTCOME;
      }

      updateState(store, '[Galleries] Batch Categories Start', { batchRunning: true });
      const results = await runBatch(
        targets.map(item => item.id),
        id => data.applyCategories(id, add, remove, createNames),
      );
      const outcome: BatchOutcome = {
        ok: targets.filter((_, index) => results[index] === null).map(item => item.id),
        failed: targets
          .map((item, index) => ({ id: item.id, title: item.title, reason: results[index] }))
          .filter((entry): entry is BatchFailure => entry.reason !== null),
      };
      const verdict = settleBatch(outcome, false, toast);

      // Same post-await rule as the delete: stamp first, then repaint the rows that took the diff.
      // Only the ids the dialog already knew can repaint here — a freshly typed name got its id
      // on the server, so it is read back below.
      ++listGeneration;
      const changed = new Set(outcome.ok);
      updateState(store, '[Galleries] Batch Categories Done', {
        items: store
          .items()
          .map(item =>
            changed.has(item.id)
              ? { ...item, categoryIds: Array.from(new Set([...item.categoryIds.filter(id => !remove.includes(id)), ...add])) }
              : item,
          ),
        batchRunning: false,
        selection: verdict === 'ok' ? [] : verdict === 'partial' ? outcome.failed.map(entry => entry.id) : store.selection(),
        selectionAnchor: verdict === 'failed' ? store.selectionAnchor() : null,
      });
      // A name minted server-side has an id only the server knows: without this read the new
      // category is missing from the filter chips and from the dialog, and typing it again
      // mints a duplicate. Only the create case pays for it.
      if (createNames.length > 0 && outcome.ok.length > 0) {
        await Promise.all([this.loadCategories(), this.load()]);
      }

      return verdict === 'failed' ? EMPTY_OUTCOME : outcome;
    },

    // The picked categories; an empty array is «Все». The list re-reads from page one with
    // the filter inside the server's keyset query, so counts stay correct past the first page.
    async setCategoryIds(ids: string[]): Promise<void> {
      if (ids.length === store.categoryIds().length && ids.every(id => store.categoryIds().includes(id))) return;
      // The question changed — every answer already in flight is void (see setQuery).
      ++listGeneration;
      updateState(store, '[Galleries] Set Category Filter', {
        categoryIds: [...ids],
        listCursor: null,
        listHasMore: false,
      });
      await this.load();
    },

    // The chosen order. Client-side over the loaded page, so no re-read and no cursor reset —
    // only the order `filteredItems` hands out changes.
    setSort(sort: GallerySort): void {
      if (sort === store.sort()) return;
      updateState(store, '[Galleries] Set Sort', { sort });
    },

    // The search box of the galleries list. The word shows at once; the server read follows the
    // typing pause, so a long query is one request instead of one per letter. The Trash has no
    // search box of its own, so it neither re-reads here nor filters by this word.
    setQuery(q: string): void {
      if (q === store.query()) return;
      // The question changed, so every answer already in flight is void — bump the stamp HERE,
      // not when the next read starts 300 ms later: a read that left during the previous word
      // otherwise still holds the current generation and paints its rows under the new word.
      ++listGeneration;
      updateState(store, '[Galleries] Set Query', { query: q, listCursor: null, listHasMore: false });
      queryTyped.next();
    },

    // Returns the created gallery id so the page can navigate into it later (P2.4 manage).
    async create(title: string, presetId?: string | null): Promise<string | null> {
      const trimmed = title.trim();
      if (!trimmed) return null;
      updateState(store, '[Galleries] Create Start', { createStatus: 'creating' });
      const result = await data.create(trimmed, presetId);
      if (!result.ok) {
        updateState(store, '[Galleries] Create Failed', { createStatus: 'error' });
        return null;
      }

      updateState(store, '[Galleries] Create Done', { createStatus: 'idle' });
      // Partial preset apply is NOT silent: the gallery exists, but the owner
      // must know which panels to finish by hand.
      if (result.presetWarnings?.length) {
        toast.error('Галерея создана, но часть настроек пресета не применилась — проверьте её настройки.');
      }

      // Independent of warnings: the passcode group may have succeeded even when
      // another group failed, and the owner must learn the never-shown code exists.
      if (result.guestPasscodeSet) {
        // The minted gate code is write-only in the UI; email invites attach it.
        toast.info('Код доступа для гостей задан. Он вложится в email-приглашение автоматически.');
      }

      // Re-read the first page instead of hand-building the list row: cover/photoCount/createdAt come from the server.
      const gen = ++listGeneration;
      const page = await data.list({ perPage: store.pageSize(), categoryId: store.categoryIds(), q: store.query() });
      // RACE-SAFE: a newer list request owns the list — do not overwrite it.
      if (page && gen === listGeneration) {
        updateState(store, '[Galleries] Load Ready', {
          items: page.items,
          status: 'ready',
          listCursor: page.nextCursor,
          listHasMore: page.hasMore,
        });
      }

      return result.gallery.galleryId;
    },

    // Duplicate an existing gallery (spec 01): the server clones settings/design/
    // scene-structure into a fresh empty gallery; re-read page one so the copy
    // appears at the top with its server-built row. Returns the new id.
    async duplicate(galleryId: string): Promise<string | null> {
      // Every call mints a NEW gallery, so a second in-flight request is never a
      // retry — it is a second copy. Collapse concurrent asks for the same source
      // (double-click, impatient re-click while the clone is still committing).
      if (duplicatesInFlight.has(galleryId)) return null;
      duplicatesInFlight.add(galleryId);

      let newId: string | null = null;
      try {
        newId = await data.duplicate(galleryId);
      } finally {
        duplicatesInFlight.delete(galleryId);
      }

      if (!newId) {
        toast.error('Не удалось дублировать галерею. Попробуйте ещё раз.');
        return null;
      }

      const gen = ++listGeneration;
      const page = await data.list({ perPage: store.pageSize(), categoryId: store.categoryIds(), q: store.query() });
      // RACE-SAFE: a newer list request owns the list — do not overwrite it.
      if (page && gen === listGeneration) {
        updateState(store, '[Galleries] Load Ready', {
          items: page.items,
          status: 'ready',
          listCursor: page.nextCursor,
          listHasMore: page.hasMore,
        });
      }

      toast.success('Галерея дублирована');
      return newId;
    },

    // The ONE door for a rename — the list row, the manage-page title and the settings field all
    // move together. A gallery opened by deep link may hold no row here; that is nothing to repaint,
    // not a reason to refuse the write.
    // `false` means ONE thing to every caller: the title was not saved, say so. A title the row
    // ALREADY carries is not a write and not a refusal — it answers `true`, because the asked-for
    // name is the name in hand. Folding those two into one `false` told the settings field
    // «Не удалось сохранить» and rolled it back to a name the server had already replaced, whenever
    // the gallery had been renamed from the list first.
    async rename(galleryId: string, title: string): Promise<boolean> {
      const trimmed = title.trim();
      if (!trimmed) return false;
      const previous = store.items();
      const current = previous.find(item => item.id === galleryId);
      if (!current) return await data.rename(galleryId, trimmed);
      if (current.title === trimmed) return true;
      // Optimistic: repaint instantly, revert on server failure.
      updateState(store, '[Galleries] Rename Optimistic', {
        items: previous.map(item => (item.id === galleryId ? { ...item, title: trimmed } : item)),
      });
      const ok = await data.rename(galleryId, trimmed);
      if (!ok) updateState(store, '[Galleries] Rename Revert', { items: previous });
      return ok;
    },

    // ONE gallery switched on or off — the ONE door every status write goes through (the status
    // dialog, «Поделиться», the settings field), so the list row and the server never disagree.
    // Silent by design: the dialog says «Галерея выключена» on its own done step, so a toast here
    // would say it twice. The row is patched where it sits — a re-read would collapse the list
    // back to page one and throw away every page «Показать ещё» has fetched.
    // ALWAYS writes, even when the local row already shows the asked status: the row is a cache
    // that may lag the server (another screen, another tab), and answering «done» without a write
    // once told a photographer «Галерея включена» over a gallery the server still hid. The server
    // UPDATE is idempotent.
    async setStatus(galleryId: string, enabled: boolean): Promise<GalleryOpFailure | null> {
      const status: GalleryStatus = enabled ? 'published' : 'hidden';
      const current = store.items().find(item => item.id === galleryId);
      // Switching ONE gallery is a per-gallery operation: a deep link into a gallery past the first
      // list page holds no row here, and that must not stop the write — it only means there is
      // nothing on this screen to repaint.
      if (!current) return await data.setStatus(galleryId, status);

      // The optimistic patch takes a fresh generation: a list read already in flight would land
      // with the old status and quietly undo the switch.
      const gen = ++listGeneration;
      updateState(store, '[Galleries] Set Status Optimistic', {
        items: store.items().map(item => (item.id === galleryId ? { ...item, status } : item)),
      });

      const failure = await data.setStatus(galleryId, status);
      // RACE-SAFE: a newer list read owns the rows now and already carries server truth.
      if (gen !== listGeneration) return failure;
      if (failure) {
        // Roll back ONLY this row inside the CURRENT list — a snapshot revert would undo whatever
        // else changed while the write was in flight.
        updateState(store, '[Galleries] Set Status Revert', {
          items: store.items().map(item => (item.id === galleryId ? { ...item, status: current.status } : item)),
        });
      }

      return failure;
    },

    async commitDelete(ids: string[]): Promise<void> {
      const results = await Promise.allSettled(ids.map(id => data.delete(id)));
      const deleted = new Set(
        ids.filter(
          (_, index) =>
            results[index].status === 'fulfilled' &&
            (results[index] as PromiseFulfilledResult<GalleryOpFailure | null>).value === null,
        ),
      );
      updateState(store, '[Galleries] Delete Done', {
        items: store.items().filter(item => !deleted.has(item.id)),
      });
      if (deleted.size < ids.length) {
        // One gallery from the row menu is not «некоторые» — it is that one gallery (spec § 28).
        toast.error(
          ids.length === 1 ? 'Не удалось удалить галерею' : 'Не удалось удалить некоторые галереи. Попробуйте ещё раз.',
        );
      }

      // The deleted galleries landed in the trash — the shell tab appears at once (s1-ac1.1).
      if (deleted.size > 0) await this.refreshTrashCount();
    },

    // The shell's «Корзина» tab source: the server count alone. A full loadTrash
    // here would flip trashStatus and re-skeleton the trash page under the user.
    async refreshTrashCount(): Promise<void> {
      const gen = ++trashCountGeneration;
      try {
        const page = await data.trash({ perPage: 1 });
        // RACE-SAFE: a newer count read owns the number now.
        if (page && gen === trashCountGeneration) {
          updateState(store, '[Galleries] Trash Count', { trashTotal: page.total });
        }
      } finally {
        // Отказ счёта — тоже ответ: иначе пустой кабинет навсегда остался бы скелетом.
        if (gen === trashCountGeneration) updateState(store, '[Galleries] Trash Count Settled', { trashTotalKnown: true });
      }
    },

    async loadTrash(): Promise<void> {
      const gen = ++trashGeneration;
      // Тёплое перечитывание (слово поиска, категория, порядок) держит строки на экране: скелет
      // рисуется только на холодном заходе (.claude/rules/loading-states.md).
      const warm = store.trashItems().length > 0;
      updateState(store, '[Galleries] Trash Load Start', warm ? {} : { trashStatus: 'loading' });
      // Поиск компилирует сервер — тот же `q`, что у списка галерей. Бейдж вкладки читает свой,
      // нефильтрованный счёт (refreshTrashCount), поэтому итог этого чтения живёт отдельно.
      const page = await data.trash({
        perPage: PER_PAGE,
        sort: store.trashSort(),
        categoryId: store.trashCategoryIds(),
        q: store.trashQuery(),
      });
      // RACE-SAFE: a newer trash read or an optimistic restore/purge/empty owns the rows now.
      if (gen !== trashGeneration) return;
      if (page) {
        // Чтение без слова и без категорий — это и есть счёт всей корзины: бейдж вкладки берёт
        // его отсюда, а суженное чтение его не трогает.
        const plain = !store.trashQuery().trim() && store.trashCategoryIds().length === 0;
        updateState(store, '[Galleries] Trash Load Ready', {
          trashItems: page.items,
          trashStatus: 'ready',
          trashCursor: page.nextCursor,
          trashHasMore: page.hasMore,
          trashListTotal: page.total,
          trashTotal: plain ? page.total : store.trashTotal(),
          trashTotalKnown: plain || store.trashTotalKnown(),
        });
      } else {
        updateState(store, '[Galleries] Trash Load Failed', { trashStatus: 'error' });
      }
    },

    // Append the next trash page (the trash load-more action).
    async loadMoreTrash(): Promise<void> {
      if (!store.trashHasMore() || store.trashLoadingMore()) return;
      const gen = ++trashGeneration;
      updateState(store, '[Galleries] Trash Load More Start', { trashLoadingMore: true });
      // Тот же порядок и тот же фильтр, что у первой страницы — иначе курсор идёт по другому списку.
      const page = await data.trash({
        cursor: store.trashCursor(),
        perPage: PER_PAGE,
        sort: store.trashSort(),
        categoryId: store.trashCategoryIds(),
        q: store.trashQuery(),
      });
      // RACE-SAFE: a newer trash read or an optimistic removal restarted the keyset — this
      // cursor page belongs to a list that no longer exists and must not append.
      if (gen !== trashGeneration) {
        updateState(store, '[Galleries] Trash Load More Stale', { trashLoadingMore: false });
        return;
      }

      if (page) {
        updateState(store, '[Galleries] Trash Load More Ready', {
          trashItems: [...store.trashItems(), ...page.items],
          trashCursor: page.nextCursor,
          trashHasMore: page.hasMore,
          trashListTotal: page.total,
          trashLoadingMore: false,
        });
      } else {
        updateState(store, '[Galleries] Trash Load More Failed', { trashLoadingMore: false });
        toast.error('Не удалось загрузить ещё. Попробуйте ещё раз.');
      }
    },

    // Optimistic: the row leaves the Trash instantly; the main list re-reads on
    // its next load, so the restored gallery reappears there without a refetch here.
    async restore(galleryId: string): Promise<boolean> {
      const previous = store.trashItems();
      const removedIndex = previous.findIndex(item => item.id === galleryId);
      if (removedIndex < 0) return false;
      const removed = previous[removedIndex];
      ++trashGeneration;
      updateState(store, '[Galleries] Restore Optimistic', {
        trashItems: previous.filter(item => item.id !== galleryId),
        trashListTotal: trashTotalAfter(store.trashListTotal(), 1),
        status: 'idle',
      });
      const ok = await data.restore(galleryId);
      if (!ok) {
        // Roll back ONLY the failed row into the CURRENT list — a full-snapshot revert
        // would resurrect rows a concurrent restore/purge removed while we were in flight.
        const current = store.trashItems().filter(item => item.id !== galleryId);
        current.splice(Math.min(removedIndex, current.length), 0, removed);
        updateState(store, '[Galleries] Restore Revert', {
          trashItems: current,
          trashListTotal: trashTotalAfter(store.trashListTotal(), -1),
        });
        toast.error('Не удалось восстановить галерею');
        return false;
      }

      toast.success(`Галерея восстановлена из корзины: «${removed.title}»`);
      void this.refreshTrashCount();
      return true;
    },

    // Force-delete one trashed gallery (irreversible). Optimistic row-leave; a
    // server failure restores the row so the user can retry.
    async purge(galleryId: string): Promise<boolean> {
      const previous = store.trashItems();
      const removedIndex = previous.findIndex(item => item.id === galleryId);
      if (removedIndex < 0) return false;
      const removed = previous[removedIndex];
      ++trashGeneration;
      updateState(store, '[Galleries] Purge Optimistic', {
        trashItems: previous.filter(item => item.id !== galleryId),
        trashListTotal: trashTotalAfter(store.trashListTotal(), 1),
      });
      const ok = await data.purge(galleryId);
      if (!ok) {
        // Roll back ONLY the failed row into the CURRENT list — a full-snapshot revert
        // would resurrect rows a concurrent restore/purge removed while we were in flight.
        const current = store.trashItems().filter(item => item.id !== galleryId);
        current.splice(Math.min(removedIndex, current.length), 0, removed);
        updateState(store, '[Galleries] Purge Revert', {
          trashItems: current,
          trashListTotal: trashTotalAfter(store.trashListTotal(), -1),
        });
        toast.error('Не удалось удалить галерею');
        return false;
      }

      toast.success(`Галерея удалена навсегда: «${removed.title}»`);
      void this.refreshTrashCount();
      return true;
    },

    // Empty the whole trash (irreversible). Optimistic clear; revert on failure. The server purges
    // one batch per request, so the request repeats while the server count keeps shrinking.
    async emptyTrash(): Promise<boolean> {
      const previous = store.trashItems();
      if (previous.length === 0) return false;
      ++trashGeneration;
      updateState(store, '[Galleries] Empty Trash Optimistic', { trashItems: [], trashListTotal: 0 });
      // Unknown before the first answer: the header count may still be in flight.
      let left: number | null = null;
      for (let request = 0; request < EMPTY_TRASH_MAX_REQUESTS; request++) {
        const ok = await data.emptyTrash();
        if (!ok) {
          // A failed bulk purge is ambiguous (part may have been purged server-side) —
          // read back server truth instead of restoring a possibly-wrong snapshot.
          toast.error('Не удалось очистить корзину. Попробуйте ещё раз.');
          await this.loadTrash();
          return false;
        }

        const before: number | null = left;
        left = (await data.trash({ perPage: 1 }))?.total ?? null;
        // Count unread, trash empty, or the batch purged nothing — another request would not help.
        if (left === null || left === 0 || (before !== null && left >= before)) break;
      }

      void this.refreshTrashCount();
      if (left === 0) {
        toast.success('Корзина очищена');
        return true;
      }

      await this.loadTrash();
      if (left !== null) toast.warn('Удалились не все галереи.. попробуйте повторить действие');
      return false;
    },

    // A row was clicked in the trash. Same law as the galleries list (spec § 13): a plain click
    // toggles and becomes the anchor, Shift ADDS the screen-order span and leaves the anchor be.
    // `order` is the axis the RANGE walks. The screen renders a subset of the store (the page's
    // own search), and a range taken over store order would then reach rows the photographer
    // cannot see — the caller hands the order it actually rendered (gallery-manage selectRangeTo).
    toggleTrashSelected(id: string, shift: boolean, order: readonly string[] = store.trashItems().map(item => item.id)): void {
      if (!order.includes(id)) return;
      const anchor = store.trashSelectionAnchor();
      const selection = store.trashSelection();
      if (shift && anchor && order.includes(anchor)) {
        const from = order.indexOf(anchor);
        const to = order.indexOf(id);
        const range = order.slice(Math.min(from, to), Math.max(from, to) + 1);
        updateState(store, '[Galleries] Trash Select Range', {
          trashSelection: Array.from(new Set([...selection, ...range])),
        });
        return;
      }

      updateState(store, '[Galleries] Trash Toggle Selected', {
        trashSelection: selection.includes(id) ? selection.filter(entry => entry !== id) : [...selection, id],
        trashSelectionAnchor: id,
      });
    },

    /** Отмечает то, что сейчас на экране корзины — страница и решает, что там после поиска. */
    selectAllVisibleTrash(ids: readonly string[]): void {
      updateState(store, '[Galleries] Trash Select All', {
        trashSelection: Array.from(new Set([...store.trashSelection(), ...ids])),
      });
    },

    clearTrashSelection(): void {
      updateState(store, '[Galleries] Trash Clear Selection', { trashSelection: [], trashSelectionAnchor: null });
    },

    // Спека § 3–4: порядок и категории корзины серверные, поэтому каждый выбор — чтение с первой
    // страницы. Курсор и «Показать ещё» сбрасывает сам loadTrash.
    async setTrashSort(sort: GallerySort): Promise<void> {
      if (sort === store.trashSort()) return;
      updateState(store, '[Galleries] Set Trash Sort', { trashSort: sort });
      await this.loadTrash();
    },

    async setTrashCategoryIds(ids: readonly string[]): Promise<void> {
      updateState(store, '[Galleries] Set Trash Category Filter', { trashCategoryIds: [...ids] });
      await this.loadTrash();
    },

    // Поиск корзины: слово видно сразу, чтение уходит после паузы — как у «Галерей». Штамп
    // поднимается здесь, а не через 300 мс: ответ на прошлое слово иначе ляжет уже под новым.
    setTrashQuery(q: string): void {
      if (q === store.trashQuery()) return;
      ++trashGeneration;
      updateState(store, '[Galleries] Set Trash Query', {
        trashQuery: q,
        trashCursor: null,
        trashHasMore: false,
        trashListTotal: null,
      });
      trashQueryTyped.next(true);
    },

    // Спека § 5: поиск, фильтр и сортировка живут в памяти страницы — экран зовёт это при уходе
    // и по «Сбросить фильтр». Отложенное чтение тоже снимается: странице оно уже не нужно.
    clearTrashFilters(): void {
      trashQueryTyped.next(false);
      updateState(store, '[Galleries] Trash Clear Filters', {
        trashSort: null,
        trashCategoryIds: [],
        trashQuery: '',
      });
    },

    // «Восстановить» и «Удалить навсегда» из плашки выбора: один запрос на галерею, четыре в
    // полёте. Вернувшиеся (или стёртые) уходят из корзины; отказавшие остаются строкой и
    // становятся новым выбором — «Повторить» гонит ровно их (спека § 15–16).
    async batchRestore(ids: readonly string[]): Promise<BatchOutcome> {
      const targets = store.trashItems().filter(item => ids.includes(item.id));
      if (store.batchRunning() || targets.length === 0) return EMPTY_OUTCOME;
      updateState(store, '[Galleries] Batch Restore Start', { batchRunning: true });
      const outcome = trashOutcome(
        targets,
        await runBatch(
          targets.map(item => item.id),
          id => data.restore(id),
        ),
      );
      const verdict = settleTrashBatch(outcome, `Восстановлено: ${outcome.ok.length}`, 'Не удалось восстановить галереи', toast);
      this.applyTrashBatch('[Galleries] Batch Restore Done', outcome, verdict);
      return verdict === 'failed' ? EMPTY_OUTCOME : outcome;
    },

    async batchPurge(ids: readonly string[]): Promise<BatchOutcome> {
      const targets = store.trashItems().filter(item => ids.includes(item.id));
      if (store.batchRunning() || targets.length === 0) return EMPTY_OUTCOME;
      updateState(store, '[Galleries] Batch Purge Start', { batchRunning: true });
      const outcome = trashOutcome(
        targets,
        await runBatch(
          targets.map(item => item.id),
          id => data.purge(id),
        ),
      );
      const verdict = settleTrashBatch(outcome, `Удалено навсегда: ${outcome.ok.length}`, 'Не удалось удалить галереи', toast);
      this.applyTrashBatch('[Galleries] Batch Purge Done', outcome, verdict);
      return verdict === 'failed' ? EMPTY_OUTCOME : outcome;
    },

    // The rows leave AFTER the awaits, so the removal owns a fresh generation: a trash page read
    // that left before the batch would otherwise land and resurrect what is already gone.
    applyTrashBatch(action: string, outcome: BatchOutcome, verdict: BatchVerdict): void {
      ++trashGeneration;
      const gone = new Set(outcome.ok);
      updateState(store, action, {
        trashItems: store.trashItems().filter(item => !gone.has(item.id)),
        trashListTotal: trashTotalAfter(store.trashListTotal(), gone.size),
        batchRunning: false,
        trashSelection:
          verdict === 'ok' ? [] : verdict === 'partial' ? outcome.failed.map(entry => entry.id) : store.trashSelection(),
        trashSelectionAnchor: verdict === 'failed' ? store.trashSelectionAnchor() : null,
      });
      if (outcome.ok.length > 0) void this.refreshTrashCount();
    },
  })),
  withHooks({
    onInit(store) {
      const destroyRef = inject(DestroyRef);
      queryTyped.pipe(debounceTime(QUERY_DEBOUNCE_MS), takeUntilDestroyed(destroyRef)).subscribe(() => void store.load());
      trashQueryTyped
        .pipe(debounceTime(QUERY_DEBOUNCE_MS), filter(Boolean), takeUntilDestroyed(destroyRef))
        .subscribe(() => void store.loadTrash());
    },
  }),
);
